import React, { useCallback, useRef, useState } from "react";
import { Form, Input, Select, Tag, Tooltip, Space, Typography } from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  RobotOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { Badge, Button, Modal, PageHeader } from "../../components/Common";
import PaginatedTable from "../../components/PaginatedTable";
import { useMutations } from "../../hooks/useApiMutation";
import { useConfirm } from "../../hooks/useConfirm";
import { useLlmProviders } from "../../hooks/useLlmProviders";
import ClientAiFields from "./components/ClientAiFields";

const { Text } = Typography;

const PROVIDER_TAG = {
  OpenAI: { color: "green", label: "OpenAI" },
  Claude: { color: "purple", label: "Claude" },
  Vision: { color: "blue", label: "Vision" },
};

const STATUS_BADGE = {
  Draft: { variant: "neutral", label: "Draft" },
  Published: { variant: "success", label: "Published" },
  Archived: { variant: "warning", label: "Archived" },
};

const AgentsList = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [llmModels, setLlmModels] = useState([]);
  const { mutate } = useMutations();
  const { confirmAction } = useConfirm();
  const { options: providerOptions } = useLlmProviders();
  const tableRef = useRef(null);

  const fetchData = useCallback(
    async (page, pageSize, search) => {
      try {
        const { data } = await mutate("fetchAgents", {
          method: "get",
          endpoint: "/v1/agents",
          params: { page, pageSize, search },
        });
        return {
          data: data?.data?.items || [],
          total: data?.data?.totalCount || 0,
        };
      } catch {
        return { data: [], total: 0 };
      }
    },
    [mutate]
  );

  const loadModels = useCallback(
    async (provider) => {
      try {
        const { data } = await mutate("fetchLlmModels", {
          method: "get",
          endpoint: "/v1/llm-models",
          params: provider ? { provider } : {},
        });
        setLlmModels(data?.data || []);
      } catch {
        setLlmModels([]);
      }
    },
    [mutate]
  );

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <Space>
          <RobotOutlined
            style={{ color: "var(--accent-blue)" }}
            aria-hidden="true"
          />
          <Button
            variant="ghost"
            style={{ padding: 0, height: "auto" }}
            onClick={() => navigate(`/agents/${record.agentId}`)}
          >
            {text}
          </Button>
          {record.isGlobal && <Tag color="geekblue">Global</Tag>}
          {record.useClientAi && (
            <Tooltip title="Runs on the client's own AI account — not limited by the subscription">
              <Tag color="gold">Client AI</Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: "Company",
      dataIndex: "companyName",
      key: "companyName",
      render: (text) => text || <Text type="secondary">—</Text>,
      responsive: ["md"],
    },
    {
      title: "Provider",
      dataIndex: "provider",
      key: "provider",
      render: (p) => {
        const cfg = PROVIDER_TAG[p] || { color: "default", label: p };
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
    {
      title: "Model",
      dataIndex: "model",
      key: "model",
      render: (m) => m || <Text type="secondary">—</Text>,
      responsive: ["lg"],
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s) => {
        const cfg = STATUS_BADGE[s] || { variant: "neutral", label: s };
        return <Badge variant={cfg.variant}>{cfg.label}</Badge>;
      },
    },
    {
      title: "Version",
      dataIndex: "currentVersion",
      key: "currentVersion",
      render: (v) => <Text code>v{v}</Text>,
      responsive: ["lg"],
    },
    {
      title: "Actions",
      key: "actions",
      width: 72,
      align: "right",
      render: (_, record) => (
        <Tooltip title="Delete">
          <Button
            variant="ghost"
            danger
            icon={<DeleteOutlined />}
            aria-label={`Delete ${record.name}`}
            onClick={() =>
              confirmAction({
                key: "deleteAgent",
                method: "delete",
                endpoint: `/v1/agents/${record.agentId}`,
                title: "Delete agent",
                content: `Delete agent "${record.name}"? This also removes all its keys, files, tools and versions.`,
                okText: "Delete",
                okType: "danger",
                onSuccess: () => tableRef.current?.reloadPage(),
              })
            }
          />
        </Tooltip>
      ),
    },
  ];

  const onCreate = () => {
    form.resetFields();
    form.setFieldsValue({ provider: "OpenAI", temperature: 0.7, useClientAi: false });
    loadModels("OpenAI");
    setOpen(true);
  };

  const handleOk = async () => {
    const values = await form.validateFields();
    // Without Client AI the agent stays global, as before: drop a client
    // picked and then unticked, and never send a key.
    const payload = values.useClientAi
      ? values
      : { ...values, companyId: undefined, clientAiApiKey: undefined };
    const { data } = await mutate("createAgent", {
      method: "post",
      endpoint: "/v1/agents",
      payload,
    });
    setOpen(false);
    const newId = data?.data?.agentId;
    if (newId) {
      navigate(`/agents/${newId}`);
    } else {
      tableRef.current?.reloadPage();
    }
  };

  const onProviderChange = (p) => {
    form.setFieldValue("model", undefined);
    loadModels(p);
  };

  return (
    <div>
      <PageHeader
        title="AI Agents"
        subtitle="Configure, version and publish the AI agents exposed to clients."
        extra={
          <Button variant="primary" icon={<PlusOutlined />} onClick={onCreate}>
            New agent
          </Button>
        }
      />

      <PaginatedTable
        ref={tableRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="agentId"
        searchPlaceholder="Search agents"
      />

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={handleOk}
        title="Create agent"
        confirmText="Create"
        width={680}
      >
        <Form form={form} layout="vertical">
          <div className="form-grid">
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: "Name is required" }]}
            >
              <Input placeholder="e.g. Invoice extractor" />
            </Form.Item>
            <Form.Item
              name="provider"
              label="Provider"
              rules={[{ required: true }]}
            >
              <Select
                onChange={onProviderChange}
                options={providerOptions}
                placeholder="Choose a provider"
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
            <Form.Item
              name="description"
              label="Description"
              className="form-grid__full"
            >
              <Input.TextArea rows={2} placeholder="What does this agent do?" />
            </Form.Item>
            <Form.Item name="model" label="Model">
              <Select
                placeholder="Choose a model"
                options={llmModels.map((m) => ({
                  value: m.modelId,
                  label: `${m.displayName} — ${m.modelId}`,
                }))}
                showSearch
                optionFilterProp="label"
                allowClear
              />
            </Form.Item>
            <Form.Item
              name="externalAgentId"
              label="External Assistant ID (optional)"
            >
              <Input placeholder="asst_... (OpenAI Assistant) or Claude agent ref" />
            </Form.Item>
            <Form.Item
              name="systemPrompt"
              label="System prompt (training)"
              className="form-grid__full"
            >
              <Input.TextArea
                rows={6}
                placeholder="Instructions that describe the agent's behaviour, tone, and constraints…"
              />
            </Form.Item>
            <div className="form-grid__full">
              <ClientAiFields withCompany />
            </div>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default AgentsList;
